import { Node, Edge } from 'reactflow';

interface ForceDirectedLayoutOptions {
  iterations?: number;
  nodeSpacing?: number;
  centerStrength?: number;
  edgeStrength?: number;
  weightFactor?: number;
}

export function applyForceDirectedLayout(
  nodes: Node[],
  edges: Edge[],
  options: ForceDirectedLayoutOptions = {}
) {
  const {
    iterations = 50,
    nodeSpacing = 200,
    centerStrength = 0.1,
    edgeStrength = 0.7,
    weightFactor = 1.0,
  } = options;

  // Create a working copy of nodes with positions
  const layoutNodes = [...nodes];
  
  // Initialize positions if not set
  layoutNodes.forEach(node => {
    if (typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
      node.position = {
        x: Math.random() * 800,
        y: Math.random() * 600,
      };
    }
  });

  // Calculate center of the graph
  const center = {
    x: layoutNodes.reduce((sum, node) => sum + node.position.x, 0) / layoutNodes.length,
    y: layoutNodes.reduce((sum, node) => sum + node.position.y, 0) / layoutNodes.length,
  };

  // Create a map of node IDs to their indices in the layoutNodes array
  const nodeIndices = new Map<string, number>();
  layoutNodes.forEach((node, index) => {
    nodeIndices.set(node.id, index);
  });

  // Run force-directed algorithm
  for (let i = 0; i < iterations; i++) {
    // Calculate repulsive forces between all nodes
    for (let a = 0; a < layoutNodes.length; a++) {
      for (let b = a + 1; b < layoutNodes.length; b++) {
        const nodeA = layoutNodes[a];
        const nodeB = layoutNodes[b];
        
        const dx = nodeB.position.x - nodeA.position.x;
        const dy = nodeB.position.y - nodeA.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        
        // Repulsive force (inverse square law)
        const force = nodeSpacing / (distance * distance);
        
        const forceX = (dx / distance) * force;
        const forceY = (dy / distance) * force;
        
        nodeA.position.x -= forceX;
        nodeA.position.y -= forceY;
        nodeB.position.x += forceX;
        nodeB.position.y += forceY;
      }
    }

    // Calculate attractive forces along edges
    for (const edge of edges) {
      const sourceIndex = nodeIndices.get(edge.source);
      const targetIndex = nodeIndices.get(edge.target);
      
      if (sourceIndex === undefined || targetIndex === undefined) continue;
      
      const sourceNode = layoutNodes[sourceIndex];
      const targetNode = layoutNodes[targetIndex];
      
      const dx = targetNode.position.x - sourceNode.position.x;
      const dy = targetNode.position.y - sourceNode.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy) || 1;
      
      // Edge weight affects the strength of attraction
      const weight = (edge.data?.weight || 1) * weightFactor;
      
      // Attractive force (linear with distance)
      const force = (distance - nodeSpacing) * edgeStrength * weight;
      
      const forceX = (dx / distance) * force;
      const forceY = (dy / distance) * force;
      
      sourceNode.position.x += forceX;
      sourceNode.position.y += forceY;
      targetNode.position.x -= forceX;
      targetNode.position.y -= forceY;
    }

    // Apply centering force
    for (const node of layoutNodes) {
      const dx = center.x - node.position.x;
      const dy = center.y - node.position.y;
      
      node.position.x += dx * centerStrength;
      node.position.y += dy * centerStrength;
    }
  }

  return { nodes: layoutNodes, edges };
}
